import type { CallAnalysisSemanticExtraction } from '@marnic/shared';
import { MiniSection } from '../primitives/MiniSection';
import { useEvidence, evidenceTargetProps } from '../EvidenceContext';
import { EvidenceBadge } from '../EvidenceBadge';

interface FactRowProps {
  evidenceId: string | undefined;
  label: string;
  text: string;
  sublabel?: string;
}

function FactRow({ evidenceId, label, text, sublabel }: FactRowProps): JSX.Element {
  const { activeId } = useEvidence();
  const target = evidenceTargetProps(evidenceId, activeId);
  return (
    <li
      id={target.id}
      className={`leading-relaxed rounded px-1 py-0.5 ${target.className}`}
    >
      <span className="text-ink-500">{label}: </span>
      <span>{text}</span>
      {sublabel && <span className="text-ink-400 italic"> — {sublabel}</span>}
      {evidenceId && (
        <span className="ml-1">
          <EvidenceBadge id={evidenceId} />
        </span>
      )}
    </li>
  );
}

export function SemanticFactsList({ semantic }: { semantic: CallAnalysisSemanticExtraction | null }): JSX.Element | null {
  if (!semantic) return null;

  const products = semantic.productsDiscussed ?? [];
  const customisations = semantic.customisations ?? [];
  const objections = semantic.objections ?? [];
  const buyingSignals = semantic.buyingSignals ?? [];
  const quantities = semantic.quantities ?? [];
  const budgets = semantic.budgetMentions ?? [];
  const deadlines = semantic.deadlines ?? [];
  const competitors = semantic.competitorsMentioned ?? [];
  const promises = semantic.promises ?? [];
  const promisesMade = semantic.promisesMade ?? [];
  const nextSteps = semantic.nextSteps ?? [];

  const total =
    products.length + customisations.length + objections.length + buyingSignals.length +
    quantities.length + budgets.length + deadlines.length + competitors.length +
    promises.length + promisesMade.length + nextSteps.length;
  if (total === 0 && !semantic.decisionProcess && !semantic.sampleAndMockupStatus) return null;

  return (
    <MiniSection title="Call facts">
      <ul className="space-y-0.5 text-xs">
        {buyingSignals.map((s, idx) => (
          <FactRow
            key={`bs-${idx}`}
            evidenceId={s.evidenceId}
            label="Buying signal"
            text={s.signal ?? s.text ?? s.verbatim ?? ''}
            sublabel={s.timestamp ?? undefined}
          />
        ))}
        {objections.map((o, idx) => (
          <FactRow
            key={`ob-${idx}`}
            evidenceId={o.evidenceId}
            label={`Objection${o.category || o.type ? ` — ${o.category ?? o.type}` : ''}`}
            text={o.verbatim ?? o.text ?? ''}
            sublabel={o.raisedAt ?? o.timestamp ?? undefined}
          />
        ))}
        {quantities.map((q, idx) => (
          <FactRow
            key={`qt-${idx}`}
            evidenceId={q.evidenceId}
            label={`Quantity${q.item ? ` — ${q.item}` : ''}`}
            text={q.countRaw ?? q.verbatim ?? ''}
          />
        ))}
        {budgets.map((b, idx) => (
          <FactRow
            key={`bg-${idx}`}
            evidenceId={b.evidenceId}
            label="Budget"
            text={b.amountRaw ?? b.verbatim ?? ''}
            sublabel={b.context ?? undefined}
          />
        ))}
        {deadlines.map((d, idx) => (
          <FactRow
            key={`dl-${idx}`}
            evidenceId={d.evidenceId}
            label="Deadline"
            text={d.dateRaw ?? d.verbatim ?? ''}
            sublabel={d.gates ?? undefined}
          />
        ))}
        {competitors.map((c, idx) => (
          <FactRow
            key={`co-${idx}`}
            evidenceId={c.evidenceId}
            label="Competitor"
            text={c.supplier ?? c.verbatim ?? ''}
            sublabel={c.context ?? undefined}
          />
        ))}
        {promises.map((p, idx) => (
          <FactRow
            key={`pr-${idx}`}
            evidenceId={p.evidenceId}
            label={`Promise (${p.by ?? p.speaker ?? '?'})`}
            text={p.what ?? p.text ?? ''}
            sublabel={p.deadline ?? undefined}
          />
        ))}
        {promisesMade.map((p, idx) => (
          <FactRow
            key={`pmade-${idx}`}
            evidenceId={undefined}
            label={`Promise (${p.speaker})`}
            text={p.text}
            sublabel={p.timestamp ?? undefined}
          />
        ))}
        {nextSteps.map((n, idx) => (
          <FactRow
            key={`ns-${idx}`}
            evidenceId={n.evidenceId}
            label={`Next step (${n.owner ?? '?'})`}
            text={n.action ?? ''}
            sublabel={n.deadline ?? n.dueDate ?? undefined}
          />
        ))}
      </ul>

      {(products.length > 0 || customisations.length > 0) && (
        <div className="flex flex-wrap gap-1 pt-1">
          {products.map((p, idx) => (
            <span key={`p-${idx}`} className="rounded bg-white border border-ink-200 px-1.5 py-0.5 text-[10px]">
              Product: {p}
            </span>
          ))}
          {customisations.map((c, idx) => (
            <span key={`c-${idx}`} className="rounded bg-white border border-ink-200 px-1.5 py-0.5 text-[10px]">
              Customisation: {c}
            </span>
          ))}
        </div>
      )}

      {(semantic.decisionProcess || semantic.sampleAndMockupStatus) && (
        <div className="space-y-1 pt-1 text-[11px]">
          {semantic.decisionProcess && (
            <div><span className="text-ink-500">Decision process: </span>{semantic.decisionProcess}</div>
          )}
          {semantic.sampleAndMockupStatus && (
            <div><span className="text-ink-500">Sample/mockup: </span>{semantic.sampleAndMockupStatus}</div>
          )}
        </div>
      )}

      {(semantic.transcriptQuality || semantic.extractionConfidence) && (
        <div className="flex items-center gap-2 pt-1 text-[10px] uppercase tracking-wide text-ink-500">
          {semantic.transcriptQuality && <span>Transcript: {semantic.transcriptQuality}</span>}
          {semantic.extractionConfidence && <span>Extraction: {semantic.extractionConfidence}</span>}
        </div>
      )}
    </MiniSection>
  );
}
